import {
  camelCase,
  capitalCase,
  kebabCase,
  pascalCase,
  snakeCase,
} from 'change-case'
import slugify from 'slugify'

function slugCase(input: string) {
  return slugify(kebabCase(input), {
    lower: true,
    strict: true,
  })
}

export enum CaseTransformer {
  Camel = 'camelCase',
  Capital = 'capitalCase',
  Param = 'paramCase',
  Pascal = 'pascalCase',
  Slug = 'slugCase',
  Snake = 'snakeCase',
}

/* @__PURE__ */
const CASE_TRANSORMERS_MAPPING = {
  [CaseTransformer.Camel]: camelCase,
  [CaseTransformer.Capital]: capitalCase,
  [CaseTransformer.Param]: kebabCase,
  [CaseTransformer.Pascal]: pascalCase,
  [CaseTransformer.Slug]: slugCase,
  [CaseTransformer.Snake]: snakeCase,
}

export function changeCase<T extends string | string[]>(
  input: T,
  toCase: CaseTransformer,
): T extends string ? string : string[] {
  if (Array.isArray(input)) {
    return input.map(item =>
      CASE_TRANSORMERS_MAPPING[toCase](item),
    ) as T extends string ? string : string[]
  }

  return CASE_TRANSORMERS_MAPPING[toCase](input) as T extends string
    ? string
    : string[]
}

function matches(patterns: (string | RegExp)[], value: string) {
  return patterns.some(pattern =>
    typeof pattern === 'string' ? pattern === value : pattern.test(value),
  )
}

function isObject(o: unknown): boolean {
  return o === Object(o) && !Array.isArray(o) && typeof o !== 'function'
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function keysChangeCase<T = any>(
  obj: any[],
  toCase: CaseTransformer,

  options?: { deep?: boolean; exclude?: (string | RegExp)[] },
): T[]

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function keysChangeCase<T = any>(
  obj: unknown,
  toCase: CaseTransformer,
  options?: { deep?: boolean; exclude?: (string | RegExp)[] },
): T

export function keysChangeCase<T>(
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  obj: any,
  toCase: CaseTransformer,

  options: { deep?: boolean; exclude?: (string | RegExp)[] } = { deep: true },
): unknown {
  if (isObject(obj)) {
    return Object.keys(obj).reduce<any>((result, key) => {
      // Check if key should be excluded from the transformation
      const transformedKey =
        options?.exclude && matches(options.exclude, key)
          ? key
          : CASE_TRANSORMERS_MAPPING[toCase](key)

      result[transformedKey] = options.deep
        ? keysChangeCase(obj[key], toCase, options)
        : obj[key]

      return result
    }, {})
  } else if (Array.isArray(obj)) {
    return obj.map(item => keysChangeCase(item, toCase, options)) as T[]
  }

  return obj
}
/* eslint-enable @typescript-eslint/no-explicit-any */
